/**
 * ============================================================================
 * ⭐ CORRECT A LABOUR RECORD (F131, Decisions 309 / 310)
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"For labor, should open an edit screen on that labor event so
 * > items can be changed?"* → *"The worker, Start and end times, Task type, Pallets at
 * > finish. Can then be recorded/tracked that they were changed and by who? This
 * > allows them to close out a task?"* → schedulers, reason required, reporting only.
 *
 * ⛔ THE RECORD ITSELF IS NOT EDITED. Saving writes a CORRECTION beside it
 * (`dbo.LaborSessionCorrection`, Pipeline migration 225) naming the scheduler, the
 * time and the reason. The kiosk's record stays as it was, and this screen shows it
 * ("As recorded") above every correction made since.
 *
 * ⭐ CLOSING OUT. A task still running has an empty end time. Filling it in closes the
 * task at that time — the worker is clocked off it — and the screen says so before
 * the save, not after.
 *
 * ⚠️ TIMES ARE THIS COMPUTER'S CLOCK, the same as every other time on the labour
 * panel and the Possible no-shows list. An unchanged field sends the recorded time
 * back EXACTLY (seconds included) rather than the minute the box can show, so saving
 * a worker change does not quietly move the start by up to 59 seconds.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
    ApiCallError, getLabourCorrection, postLabourCorrection,
    type LabourCorrectionViewDto, type LabourSessionDto,
} from '@/api/client';

/** `<input type="datetime-local">` wants local wall-clock "YYYY-MM-DDTHH:mm". */
function toLocalInput(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function when(iso: string | null): string {
    if (!iso) return 'still running';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

/** ⭐ One per opening of the screen — Pipeline replays a retried save instead of writing twice. */
function newEventId(): string {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `lc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface FormState {
    pinUserId: string;
    taskTypeId: string;
    started: string;
    finished: string;
    pallets: string;
    reason: string;
}

export function LabourCorrectionDialog({
    open, facilityCode, session, getToken, onClose, onSaved,
}: {
    open: boolean;
    facilityCode: string;
    session: LabourSessionDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    /** Called after a correction is recorded, so the panel reloads. */
    onSaved: (closedOut: boolean) => void;
}) {
    const [view, setView] = useState<LabourCorrectionViewDto | null>(null);
    const [loadErr, setLoadErr] = useState<string | null>(null);
    const [form, setForm] = useState<FormState | null>(null);
    /** The values the form started from, so an unchanged field is sent back exactly. */
    const [base, setBase] = useState<{ form: FormState; startedIso: string; finishedIso: string | null } | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveErr, setSaveErr] = useState<string[] | null>(null);
    const eventId = useMemo(() => (open ? newEventId() : ''), [open, session?.labourSessionId]);

    const sessionId = session?.labourSessionId ?? null;
    useEffect(() => {
        if (!open || !sessionId) return;
        let alive = true;
        setView(null); setForm(null); setBase(null); setLoadErr(null); setSaveErr(null);
        (async () => {
            try {
                const v = await getLabourCorrection(await getToken(), facilityCode, sessionId);
                if (!alive) return;
                /* ⭐ THE FORM STARTS FROM THE LATEST CORRECTION, ELSE FROM THE RECORD —
                   a second correction corrects the first, not the kiosk. */
                const cur = v.latest;
                const startedIso = cur?.startedUtc ?? v.session.startedUtc;
                const finishedIso = cur ? cur.finishedUtc : v.session.finishedUtc;
                const f: FormState = {
                    pinUserId: String(cur?.pinUserId ?? v.session.pinUserId),
                    taskTypeId: String(cur?.taskTypeId ?? v.session.taskTypeId),
                    started: toLocalInput(startedIso),
                    finished: toLocalInput(finishedIso),
                    pallets: cur?.palletsAtFinish != null ? String(cur.palletsAtFinish) : '',
                    reason: '',
                };
                setView(v); setForm(f); setBase({ form: f, startedIso, finishedIso });
            } catch (err) {
                if (alive) setLoadErr(err instanceof Error ? err.message : String(err));
            }
        })();
        return () => { alive = false; };
    }, [open, sessionId, facilityCode, getToken]);

    const running = view ? view.session.finishedUtc === null : false;
    const closesOut = running && !!form?.finished;
    const changed = !!(form && base && (
        form.pinUserId !== base.form.pinUserId || form.taskTypeId !== base.form.taskTypeId
        || form.started !== base.form.started || form.finished !== base.form.finished
        || form.pallets !== base.form.pallets));
    const reasonOk = !!form && form.reason.trim().length > 0
        && form.reason.trim().length <= (view?.limits.reasonMax ?? 200);
    const canSave = !!view && !!form && !view.session.isShift && changed && reasonOk && !saving;

    const set = (k: keyof FormState) => (e: { target: { value: string } }) =>
        setForm(f => (f ? { ...f, [k]: e.target.value } : f));

    const save = async () => {
        if (!view || !form || !base || !sessionId) return;
        setSaving(true); setSaveErr(null);
        try {
            const startedUtc = form.started === base.form.started ? base.startedIso : fromLocalInput(form.started);
            const finishedUtc = form.finished === base.form.finished ? base.finishedIso : fromLocalInput(form.finished);
            if (!startedUtc) { setSaveErr(['The start time is missing or not a time.']); return; }
            const pallets = form.pallets.trim() === '' ? null : Number(form.pallets);
            const out = await postLabourCorrection(await getToken(), sessionId, {
                facilityCode,
                clientEventId: eventId,
                reason: form.reason.trim(),
                pinUserId: Number(form.pinUserId),
                taskTypeId: Number(form.taskTypeId),
                startedUtc,
                finishedUtc,
                palletsAtFinish: pallets,
            });
            onSaved(out.closedOut);
        } catch (err) {
            if (err instanceof ApiCallError) {
                const issues = Array.isArray(err.details.issues)
                    ? (err.details.issues as unknown[]).filter((x): x is string => typeof x === 'string') : [];
                setSaveErr(issues.length > 0 ? issues : [err.message]);
            } else {
                setSaveErr([err instanceof Error ? err.message : String(err)]);
            }
        } finally {
            setSaving(false);
        }
    };

    const workerName = (id: number) => view?.workers.find(w => w.pinUserId === id)?.name ?? `Worker ${id}`;
    const taskName = (id: number) => view?.tasks.find(t => t.taskTypeId === id)?.name ?? `Task ${id}`;
    const field = 'w-full rounded border bg-background px-2 py-1 text-sm';

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Correct labor record</DialogTitle>
                    <DialogDescription>
                        {session?.doorName ?? 'No door'} — {session?.workerName ?? 'Unnamed worker'}
                        {session?.taskLabel ? ` · ${session.taskLabel}` : ''}.
                        {' '}The record stays as the kiosk made it; your correction is saved beside it with your name and reason.
                    </DialogDescription>
                </DialogHeader>

                {loadErr && (
                    <div className="rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-sm text-destructive">
                        {loadErr}
                    </div>
                )}
                {!loadErr && !view && (
                    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading the record…
                    </div>
                )}

                {view && form && (
                    <div className="space-y-3 text-sm">
                        <div className="rounded border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground">As recorded: </span>
                            {workerName(view.session.pinUserId)} · {taskName(view.session.taskTypeId)} ·{' '}
                            {when(view.session.startedUtc)} → {when(view.session.finishedUtc)}
                        </div>

                        {view.session.isShift ? (
                            <div className="rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs">
                                This is the worker’s shift clock, not a task. Shifts are not corrected here.
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                <label className="col-span-2 space-y-0.5">
                                    <span className="text-xs text-muted-foreground">Worker</span>
                                    <select className={field} value={form.pinUserId} onChange={set('pinUserId')}>
                                        {view.workers.map(w => (
                                            <option key={w.pinUserId} value={String(w.pinUserId)}>
                                                {w.name}{w.active ? '' : ' (inactive)'}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="col-span-2 space-y-0.5">
                                    <span className="text-xs text-muted-foreground">Task</span>
                                    <select className={field} value={form.taskTypeId} onChange={set('taskTypeId')}>
                                        {!view.tasks.some(t => String(t.taskTypeId) === form.taskTypeId) && (
                                            <option value={form.taskTypeId}>{taskName(Number(form.taskTypeId))}</option>
                                        )}
                                        {view.tasks.map(t => (
                                            <option key={t.taskTypeId} value={String(t.taskTypeId)}>{t.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="space-y-0.5">
                                    <span className="text-xs text-muted-foreground">Started</span>
                                    <input type="datetime-local" className={field} value={form.started} onChange={set('started')} />
                                </label>
                                <label className="space-y-0.5">
                                    <span className="text-xs text-muted-foreground">
                                        Finished{running ? ' (empty = still running)' : ''}
                                    </span>
                                    <input type="datetime-local" className={field} value={form.finished} onChange={set('finished')} />
                                </label>
                                <label className="space-y-0.5">
                                    <span className="text-xs text-muted-foreground">Pallets at finish</span>
                                    <input type="number" min={0} max={view.limits.palletsMax} step={1}
                                           className={field} value={form.pallets} onChange={set('pallets')} />
                                </label>
                                {closesOut && (
                                    <div className="col-span-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs">
                                        This task is still running. Saving closes it out at the finish time — the worker is clocked off it.
                                    </div>
                                )}
                                <label className="col-span-2 space-y-0.5">
                                    <span className="text-xs text-muted-foreground">
                                        Reason (required) · {form.reason.trim().length}/{view.limits.reasonMax}
                                    </span>
                                    <textarea className={field} rows={2} maxLength={view.limits.reasonMax}
                                              value={form.reason} onChange={set('reason')}
                                              placeholder="e.g. Worker forgot to clock off at 3:10" />
                                </label>
                            </div>
                        )}

                        {view.history.length > 0 && (
                            <div className="space-y-1">
                                <div className="text-xs font-semibold">Corrections ({view.history.length})</div>
                                <ul className="max-h-32 space-y-1 overflow-y-auto">
                                    {view.history.map(h => (
                                        <li key={h.laborSessionCorrectionId} className="rounded border px-2 py-1 text-xs">
                                            <div>
                                                {h.workerName} · {h.taskName} · {when(h.startedUtc)} → {when(h.finishedUtc)}
                                                {h.palletsAtFinish != null ? ` · ${h.palletsAtFinish} plt` : ''}
                                                {h.closedOpenSession ? ' · closed out' : ''}
                                            </div>
                                            <div className="text-muted-foreground">
                                                {h.correctedBy}, {when(h.correctedUtc)} — “{h.reason}”
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {saveErr && (
                            <ul className="list-disc space-y-0.5 rounded border border-destructive/50 bg-destructive/5 py-1.5 pl-6 pr-2 text-xs text-destructive">
                                {saveErr.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={() => { void save(); }} disabled={!canSave}
                            title={!changed ? 'Nothing has been changed yet' : !reasonOk ? 'A reason is required' : undefined}>
                        {saving ? 'Saving…' : closesOut ? 'Save & close out task' : 'Save correction'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
